/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { AuditLogIdentityType } from "~/generated/graphql";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { AuditLogsQuery } from "./types";

type DefinitionKey = "operation" | "target" | "identityType" | "identity";

type AuditLogsQueryState = AuditLogsQuery & QueryState;

type Definition = AppliedFilterDefinition<AuditLogsQueryState, DefinitionKey>;

const operationLabels: Record<string, string> = {
  create: "Create",
  update: "Update",
  delete: "Delete",
  execute: "Execute",
};

const targetLabels: Record<string, string> = {
  resource: "Resource",
  resource_group: "Resource Group",
  plan: "Plan",
  cloud_account: "Cloud Account",
  notification_group: "Notification Group",
  user: "User",
  api_key: "API Key",
  role: "Role",
};

const identityTypeLabels: Record<string, string> = {
  [AuditLogIdentityType.User]: "User",
  [AuditLogIdentityType.ApiKey]: "API Key",
  [AuditLogIdentityType.System]: "System",
};

export const auditLogsAppliedFilters: Definition[] = [
  {
    key: "target",
    label: "Target",
    enableValuePopover: false,
    getValues: (queryState) => (queryState.target ? [queryState.target] : []),
    getValueDisplay: (queryState) =>
      queryState.target
        ? targetLabels[queryState.target] ?? queryState.target
        : "",
    clear: () => ({
      target: undefined,
    }),
  },
  {
    key: "operation",
    label: "Operation",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.operation ? [queryState.operation] : [],
    getValueDisplay: (queryState) =>
      queryState.operation
        ? operationLabels[queryState.operation] ?? queryState.operation
        : "",
    clear: () => ({
      operation: undefined,
    }),
  },
  {
    key: "identityType",
    label: "Identity Type",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.identityType ? [queryState.identityType] : [],
    getValueDisplay: (queryState) =>
      queryState.identityType
        ? identityTypeLabels[queryState.identityType] ?? queryState.identityType
        : "",
    clear: () => ({
      identityType: undefined,
      identity: undefined,
    }),
  },
  {
    key: "identity",
    label: "Identity",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.identity ? [queryState.identity] : [],
    getValueDisplay: (queryState) => queryState.identity ?? "",
    clear: () => ({
      identity: undefined,
    }),
  },
];
